import "@/pdfWorker";

import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

import Loader from "@/components/ui/loader";

type PdfPreviewProps = {
  url: string; // Your signed S3 URL
  scale?: number; // Optional scale factor for quality
};

export function PdfPreview({ url, scale = 1.5 }: PdfPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const renderPdf = async () => {
      setLoading(true);
      try {
        const loadingTask = pdfjsLib.getDocument({ url });
        const pdf = await loadingTask.promise;

        if (isCancelled) return;

        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const context = canvas.getContext("2d");
        if (!context) return;

        const renderContext = {
          canvasContext: context,
          viewport,
          canvas,
        };

        await page.render(renderContext).promise;
        if (!isCancelled) {
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          setError("Failed to render PDF.");
          setLoading(false);
        }
        console.error("PDF preview error:", err);
      }
    };

    renderPdf();

    return () => {
      isCancelled = true;
    };
  }, [url, scale]);

  if (error) {
    return <div className="text-red-500 text-sm">{error}</div>;
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1,
          }}
        >
          <Loader />
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          transform: "scale(0.5)", // Optional: visually zoom in
          transformOrigin: "top left", // anchor point for scaling
        }}
      />
    </div>
  );
}
